import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      setFormStatus({
        type: 'error',
        message: 'All fields are required.'
      });
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setFormStatus({
        type: 'error',
        message: 'Please enter a valid email address.'
      });
      return;
    }

    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      // Use URLSearchParams for form data
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('form-name', 'contact');
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);

      // Send to Netlify
      const response = await fetch('/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString(),
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (response.ok) {
        setFormStatus({
          type: 'success',
          message: 'Message sent successfully! We will contact you soon.'
        });
        setFormData({ name: '', email: '', message: '' });
      } else {
        const responseText = await response.text();
        console.error('Response text:', responseText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus({
        type: 'error',
        message: 'Failed to send message. Please try again or contact us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-16 md:pt-20">
      {/* Military Hero Section */}
      <section className="section-military bg-black">
        <div className="container-military text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-black tracking-tighter mb-6 text-gradient-military"
          >
            {t('contact.page.title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl text-military-silver max-w-3xl mx-auto font-military-mono"
          >
            {t('contact.page.subtitle')}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="w-32 h-1 bg-gradient-to-r from-military-accent to-military-accent mx-auto mt-6"
          />
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-military bg-gray-900">
        <div className="container-military">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="military-panel p-6 md:p-8"
            >
              <h2 className="text-2xl md:text-3xl font-black tracking-tighter text-gradient-military mb-6">{t('contact.form.title')}</h2>
              
                             <form 
                 name="contact" 
                 method="POST" 
                 data-netlify="true" 
                 netlify-honeypot="bot-field"
                 onSubmit={handleSubmit}
                 className="space-y-6"
               >
                 <input type="hidden" name="form-name" value="contact" />
                 <p className="hidden">
                   <label>
                     Don't fill this out if you're human: <input name="bot-field" />
                   </label>
                 </p>
                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-military-accent mb-2 font-military-mono">
                    {t('contact.form.name')} *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-military-navy/50 border border-military-accent text-white placeholder-military-silver focus:ring-2 focus:ring-military-accent focus:border-transparent transition-all duration-300 font-military-mono"
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-military-accent mb-2 font-military-mono">
                    {t('contact.form.email')} *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-military-navy/50 border border-military-accent text-white placeholder-military-silver focus:ring-2 focus:ring-military-accent focus:border-transparent transition-all duration-300 font-military-mono"
                    placeholder="Enter your email address"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-bold text-military-accent mb-2 font-military-mono">
                    {t('contact.form.message')} *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="5"
                    className="w-full px-4 py-3 bg-military-navy/50 border border-military-accent text-white placeholder-military-silver focus:ring-2 focus:ring-military-accent focus:border-transparent transition-all duration-300 resize-none font-military-mono"
                    placeholder="Tell us about your project or inquiry"
                    required
                  />
                </div>

                {/* Form Status Message */}
                {formStatus.message && (
                  <div className={`p-4 border ${
                    formStatus.type === 'success' 
                      ? 'bg-military-success/20 text-military-success border-military-success' 
                      : 'bg-military-warning/20 text-military-warning border-military-warning'
                  } font-military-mono`}>
                    {formStatus.message}
                  </div>
                )}

                                 <button
                   type="submit"
                   disabled={isSubmitting}
                   className={`w-full btn-military-primary py-4 text-lg transition-all duration-300 ${
                     isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'
                   }`}
                 >
                   {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
                 </button>
              </form>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="space-y-6 md:space-y-8"
            >
              {/* Company Info */}
              <div className="military-panel p-6 md:p-8">
                <h3 className="text-xl md:text-2xl font-black tracking-tighter text-gradient-military mb-6">{t('contact.company.title')}</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-4">
                    <div className="w-6 h-6 bg-military-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-black text-sm">📍</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-military-accent mb-1 font-military-mono">ADDRESS - DUBAI</h4>
                      <p className="text-military-silver font-military-mono text-sm">Meydan Grandstand, 6th floor, Meydan Road, Nad Al Sheba, Dubai, U.A.E.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-6 h-6 bg-military-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-black text-sm">📞</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-military-accent mb-1 font-military-mono">PHONE</h4>
                      <p className="text-military-silver font-military-mono text-sm">+971 52 465 7009</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <div className="w-6 h-6 bg-military-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-black text-sm">✉️</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-military-accent mb-1 font-military-mono">{t('contact.company.email.title')}</h4>
                      <p className="text-military-silver font-military-mono text-sm">sales@centronaero.com</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Contact */}
              <div className="military-panel p-6 md:p-8">
                <h3 className="text-xl md:text-2xl font-black tracking-tighter text-gradient-military mb-6">QUICK CONTACT</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-4 p-4 bg-military-navy/50 rounded-lg border border-military-accent/30">
                    <div className="w-12 h-12 bg-military-accent rounded-full flex items-center justify-center">
                      <span className="text-black text-xl">🚀</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-military-accent font-military-mono">TECHNICAL SUPPORT</h4>
                      <p className="text-military-silver text-sm font-military-mono">24/7 support for critical systems</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 p-4 bg-military-navy/50 rounded-lg border border-military-accent/30">
                    <div className="w-12 h-12 bg-military-accent rounded-full flex items-center justify-center">
                      <span className="text-black text-xl">💼</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-military-accent font-military-mono">SALES INQUIRIES</h4>
                      <p className="text-military-silver text-sm font-military-mono">Get pricing and availability</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-4 p-4 bg-military-navy/50 rounded-lg border border-military-accent/30">
                    <div className="w-12 h-12 bg-military-accent rounded-full flex items-center justify-center">
                      <span className="text-black text-xl">🤝</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-military-accent font-military-mono">PARTNERSHIPS</h4>
                      <p className="text-military-silver text-sm font-military-mono">Strategic collaboration opportunities</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact; 